import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import config from '@/config'
import { getUserInfo, toast } from './bridge'
import { pickAppCommonParams } from './requestParams'
import Loading from '@/components/Loading';
import { t } from 'i18next'

export type TAxiosConfigExtra = {
  loading?: boolean;
  /** 为 true 时不弹出 code / 网络错误 toast。用于 i18n 尚未 init 的引导请求。 */
  silent?: boolean;
  transformData?: (data: any) => any;
};

const axiosInstance = axios.create({
  baseURL: config.net.host,
  timeout: 30000
})

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const auth = await getUserInfo()
    const bridgePackageName = window.bridge ? auth?.packageName || '' : ''
    const authParams = { ...(auth ?? {}) }
    if (!window.bridge) delete authParams.packageName
    config.params = {
      ...config.params,
      ...authParams,
      ...['lang', 'deviceId'].reduce<{ [x: string]: string }>((ret, d) => {
        switch (d) {
          case 'lang':
            ret.language = localStorage.getItem(d) || ''
            break
          default:
            ret[d] = localStorage.getItem(d) || ''
        }
        return ret
      }, {}),
      ...pickAppCommonParams(),
      ...(bridgePackageName ? { packageName: bridgePackageName } : {})
    }
    config.headers.uid = auth?.uid
    config.headers.ticket = auth?.ticket
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (res: AxiosResponse & { config: TAxiosConfigExtra }) => {
    const { code, message } = res.data as IResponse<unknown>;
    if (code !== 200 && !res.config.silent) {
      toast(message);
    }
    if (res.config.loading) {
      Loading.hide();
    }
    if (res?.data?.data) {
      // 列表放回的顶层不加，特殊处理
      if ('whetherLastPage' in res.data.data && 'list' in res.data.data) {
        res.data.data = {
          ...res.data.data,
          dl_list: res.data.data.list,
          dl_whetherLastPage: res.data.data.whetherLastPage,
        };
        delete res.data.data.list;
        delete res.data.data.whetherLastPage;
      }
      res.data.data = handleResWithPrefix(res.data.data);
    }
    return res;
  },
  (error: AxiosError) => {
    const silent = (error.config as TAxiosConfigExtra | undefined)?.silent
    if (silent) return
    // 处理网络超时
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      toast(t('Common.NetworkError'))
    } else {
      toast(t('Common.SystemError'))
    }
  }
)

const handler = {
  get: function (target: any, prop: any) {
    if (prop === '__target__') {
      return target;
    }

    const prefix = process.env.REACT_APP_ATTRIBUTE_PREFIX;
    return target[`${prefix}${prop}`];
  },
  set: function (target: any, prop: any, value: any) {
    const prefix = process.env.REACT_APP_ATTRIBUTE_PREFIX;
    target[`${prefix}${prop}`] = value;
    return true;
  },
};

export function handleResWithPrefix(data: any) {
  if (!data || typeof data !== 'object') return data;

  if (Array.isArray(data)) {
    data = data.map((item) => handleResWithPrefix(item));
    return data;
  }

  for (const key in data) {
    data[key] = handleResWithPrefix(data[key]);
  }
  return new Proxy(data, handler);
}

export async function service<T = any, D = any, R = AxiosResponse<T, D>>(
  config: AxiosRequestConfig<D> &
    TAxiosConfigExtra & {
      track?: { name: string; ext?: { [key: string]: any } }
    }
): Promise<R> {
  // const st = Date.now()
  return await axiosInstance(config)
  // const et = Date.now()
  // if (config.track && config.track.name) {
    // window.ta.track('ga_api', {
    //   name: config.track.name,
    //   url: config.url,
    //   method: (config.method || 'GET').toUpperCase(),
    //   params: JSON.stringify(config.params || {}),
    //   data: JSON.stringify(config.data || {}),
    //   duration: et - st
    // })
  // }
}
